import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay, EffectCoverflow } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-coverflow';
import { 
  FaUsers, FaBed, FaShower, FaTv, FaUtensils, 
  FaFire, FaSolarPanel, FaCar, FaExpand, FaArrowRight 
} from 'react-icons/fa';
import fleetData from '../data/fleet.json';

const FleetNew = () => {
  const [selectedCaravan, setSelectedCaravan] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState('');

  // Use fleet data directly from JSON - show only featured caravans
  const caravans = fleetData.slice(0, 3);

  // Icon mapping for features
  const getFeatureIcon = (featureText) => {
    const text = featureText.toLowerCase();
    if (text.includes('bed') || text.includes('sleep')) return <FaBed />;
    if (text.includes('kitchen') || text.includes('cook')) return <FaUtensils />;
    if (text.includes('bath') || text.includes('shower') || text.includes('toilet')) return <FaShower />;
    if (text.includes('tv') || text.includes('entertainment') || text.includes('sound')) return <FaTv />;
    if (text.includes('solar') || text.includes('power')) return <FaSolarPanel />;
    if (text.includes('air') || text.includes('ac') || text.includes('climate') || text.includes('heating')) return <FaFire />;
    if (text.includes('seat') || text.includes('people') || text.includes('capacity')) return <FaUsers />;
    return <FaCar />;
  };

  const openLightbox = (image) => {
    setLightboxImage(image);
    setLightboxOpen(true);
  };

  return (
    <section id="fleet" className="section-padding bg-gradient-to-b from-primary-50 to-white overflow-hidden">
      <div className="container-custom">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="text-accent-600 font-semibold text-sm uppercase tracking-wide">
            Our Fleet
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-primary-800 mt-4 mb-6">
            Choose Your Perfect
            <span className="text-accent-600"> Holiday On Wheel</span>
          </h2>
          <p className="text-xl text-primary-600 max-w-3xl mx-auto">
            Fully equipped caravans with modern amenities, designed to make your journey 
            as comfortable as your destination
          </p>
        </div>

        {/* Caravan Selection Tabs */}
        <div className="flex justify-center mb-12 space-x-4">
          {caravans.map((caravan, index) => (
            <button
              key={index}
              onClick={() => setSelectedCaravan(index)}
              className={`px-6 py-3 rounded-full font-semibold transition-all transform hover:scale-105 ${
                selectedCaravan === index
                  ? 'bg-accent-500 text-white shadow-lg scale-105'
                  : 'bg-white text-primary-700 hover:bg-primary-100 shadow'
              }`}
            >
              {caravan.capacity}
            </button>
          ))}
        </div>

        {/* Hero Image with Swiper */}
        <div className="mb-12">
          <Swiper
            effect={'coverflow'}
            grabCursor={true}
            centeredSlides={true}
            slidesPerView={'auto'}
            coverflowEffect={{
              rotate: 50,
              stretch: 0,
              depth: 100,
              modifier: 1,
              slideShadows: true,
            }}
            autoplay={{
              delay: 3000,
              disableOnInteraction: false,
            }}
            pagination={{ clickable: true }}
            navigation={true}
            modules={[EffectCoverflow, Pagination, Navigation, Autoplay]}
            className="fleet-swiper"
          >
            {caravans[selectedCaravan].images.map((image, index) => (
              <SwiperSlide key={index} style={{ width: '70%', maxWidth: '800px' }}>
                <div 
                  className="relative rounded-3xl overflow-hidden shadow-2xl cursor-pointer group"
                  onClick={() => openLightbox(image)}
                >
                  <img
                    src={image}
                    alt={`${caravans[selectedCaravan].name} - View ${index + 1}`}
                    className="w-full h-[400px] md:h-[500px] object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <FaExpand className="text-white text-4xl" />
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        {/* Caravan Details */}
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          <div className="grid lg:grid-cols-2">
            {/* Left - Info */}
            <div className="p-8 md:p-12 bg-gradient-to-br from-primary-50 to-accent-50">
              <h3 className="text-3xl md:text-4xl font-bold text-primary-800 mb-3">
                {caravans[selectedCaravan].name}
              </h3>
              <p className="text-xl text-primary-600 mb-8">
                {caravans[selectedCaravan].description}
              </p>

              {/* Quick Features */}
              <div className="grid grid-cols-3 gap-4 mb-8">
                {caravans[selectedCaravan].features.slice(0, 6).map((feature, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-xl p-4 text-center shadow-md hover:shadow-lg transition-shadow"
                  >
                    <div className="text-3xl text-accent-500 mb-2">{getFeatureIcon(feature)}</div>
                    <div className="text-xs font-semibold text-primary-700">{feature}</div>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-4">
                <Link
                  to={`/fleet/${fleetData[selectedCaravan].id}`}
                  className="btn-primary inline-flex items-center gap-2"
                >
                  View Full Details
                  <FaArrowRight />
                </Link>
                <a
                  href="tel:+918380866111"
                  className="btn-secondary"
                >
                  Call for Details
                </a>
              </div>
            </div>

            {/* Right - Amenities */}
            <div className="p-8 md:p-12 bg-white">
              <h4 className="text-2xl font-bold text-primary-800 mb-6">
                Amenities & Features
              </h4>
              <div className="space-y-4 mb-8">
                {caravans[selectedCaravan].amenities.map((amenity, index) => (
                  <div
                    key={index}
                    className="flex items-start space-x-3"
                  >
                    <div className="flex-shrink-0 w-6 h-6 bg-forest-100 rounded-full flex items-center justify-center mt-1">
                      <div className="w-3 h-3 bg-forest-500 rounded-full" />
                    </div>
                    <p className="text-primary-700">{amenity}</p>
                  </div>
                ))}
              </div>

              <div className="p-6 bg-accent-50 rounded-xl border-2 border-accent-200">
                <h5 className="font-bold text-primary-800 mb-3">What's Included:</h5>
                <ul className="text-sm text-primary-700 space-y-2">
                  {caravans[selectedCaravan].included.map((item, index) => (
                    <li key={index}>✓ {item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <div 
          className="fixed inset-0 bg-black/95 z-[100] flex items-center justify-center p-4"
          onClick={() => setLightboxOpen(false)}
        >
          <button
            onClick={() => setLightboxOpen(false)}
            className="absolute top-4 right-4 text-white text-4xl hover:text-accent-400 transition-colors"
          >
            ×
          </button>
          <img
            src={lightboxImage}
            alt="Full size view"
            className="max-w-full max-h-full object-contain rounded-lg"
          />
        </div>
      )}
    </section>
  );
};

export default FleetNew;
